import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { loansAPI } from '../../services/api';
import { Loan, LoanStatus, LoanPayment } from '../../types';
import { useTranslation } from 'react-i18next';
import { useCompany } from '../CompanyContext';
import {
  ArrowLeftIcon,
  CreditCardIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CurrencyDollarIcon,
  UserIcon,
  MapPinIcon,
  CalendarIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import LoanPaymentModal from './LoanPaymentModal';

interface PaymentAmounts {
  totalPayments: number;
  totalPrincipal: number;
  totalInterest: number;
  totalLateFees: number;
  paymentCount: number;
  lastPaymentDate: Date | null;
}

interface PaymentHistoryItem {
  id: string;
  amount: number;
  principalAmount: number;
  interestAmount: number;
  lateFeeAmount: number;
  paymentDate: Date;
  paymentMethod: string;
  notes: string | null;
  receiptNumber: string | null;
  overdueDays: number;
}

const LoanDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { selectedCompany } = useCompany();
  const { t } = useTranslation();
  const [loan, setLoan] = useState<Loan | null>(null);
  const [paymentAmounts, setPaymentAmounts] = useState<PaymentAmounts | null>(null);
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  useEffect(() => {
    if (id && selectedCompany) {
      fetchLoan();
    }
  }, [id, selectedCompany]);

  const fetchLoan = async () => {
    if (!id || !selectedCompany) return;

    try {
      setLoading(true);
      setError(null);
      
      // Fetch loan details and payment data in parallel
      const [loanData, paymentAmountsData, paymentHistoryData] = await Promise.all([
        loansAPI.getById(selectedCompany.id, id),
        loansAPI.getPaymentAmounts(selectedCompany.id, id),
        loansAPI.getPaymentHistory(selectedCompany.id, id),
      ]);
      
      setLoan(loanData);
      setPaymentAmounts(paymentAmountsData);
      setPaymentHistory(paymentHistoryData);
    } catch (err) {
      setError(t('loans.errorLoadingLoan'));
      console.error('Error fetching loan:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: LoanStatus) => {
    switch (status) {
      case LoanStatus.ACTIVE:
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case LoanStatus.COMPLETED:
        return <CheckCircleIcon className="h-5 w-5 text-blue-500" />;
      case LoanStatus.DEFAULTED:
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case LoanStatus.CANCELLED:
        return <ExclamationTriangleIcon className="h-5 w-5 text-gray-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status: LoanStatus) => {
    const baseClasses = "inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border";
    
    switch (status) {
      case LoanStatus.ACTIVE:
        return `${baseClasses} bg-green-100 text-green-800 border-green-200`;
      case LoanStatus.COMPLETED:
        return `${baseClasses} bg-blue-100 text-blue-800 border-blue-200`;
      case LoanStatus.DEFAULTED:
        return `${baseClasses} bg-red-100 text-red-800 border-red-200`;
      case LoanStatus.CANCELLED:
        return `${baseClasses} bg-gray-100 text-gray-800 border-gray-200`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800 border-gray-200`;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString();
  };

  const formatPercentage = (rate: number) => {
    return `${(rate * 100).toFixed(2)}%`;
  };

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
    fetchLoan(); // Refresh loan data after payment
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error || !loan) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('loans.errorLoadingLoan')}</h3>
          <p className="mt-1 text-sm text-gray-500">{error || t('common.notFound')}</p>
          <button
            onClick={() => navigate('/loans')}
            className="mt-4 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200"
          >
            {t('actions.goBack')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/loans')}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <ArrowLeftIcon className="h-5 w-5 text-gray-400" />
          </button>
          <div className="flex items-center space-x-3">
            <CreditCardIcon className="h-8 w-8 text-gray-400" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t('loans.loanDetails')}
              </h1>
              <p className="text-sm text-gray-500">
                {t('loans.loanId')}: {loan.id}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowPaymentModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <CurrencyDollarIcon className="h-4 w-4 mr-2" />
            {t('loans.recordPayment')}
          </button>
          <Link
            to={`/loans/${loan.id}/edit`}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <PencilIcon className="h-4 w-4 mr-2" />
            {t('actions.edit')}
          </Link>
        </div>
      </div>

      {/* Loan Overview */}
      <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg p-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Status */}
          <div className="text-center lg:text-left">
            <div className="flex items-center justify-center lg:justify-start space-x-2 mb-2">
              {getStatusIcon(loan.status)}
              <span className={getStatusBadge(loan.status)}>
                {t(`loans.status.${loan.status.toUpperCase()}`)}
              </span>
            </div>
            <div className="text-sm text-gray-500">{t('loans.detail.status')}</div>
          </div>

          {/* Principal Amount */}
          <div className="text-center lg:text-left">
            <div className="text-2xl font-bold text-gray-900 mb-1">
              {formatCurrency(loan.principalAmount)}
            </div>
            <div className="text-sm text-gray-500">{t('loans.detail.principalAmount')}</div>
          </div>

          {/* Current Balance */}
          <div className="text-center lg:text-left">
            <div className="text-2xl font-bold text-indigo-600 mb-1">
              {formatCurrency(loan.currentBalance)}
            </div>
            <div className="text-sm text-gray-500">{t('loans.detail.currentBalance')}</div>
          </div>

          {/* Total Paid */}
          <div className="text-center lg:text-left">
            <div className="text-2xl font-bold text-green-600 mb-1">
              {formatCurrency(paymentAmounts?.totalPayments || loan.totalPaid)}
            </div>
            <div className="text-sm text-gray-500">{t('loans.detail.totalPaid')}</div>
            {paymentAmounts && (
              <div className="text-xs text-gray-400">
                {paymentAmounts.paymentCount} {t('loans.detail.paymentsCount')}
              </div>
            )}
          </div>
        </div>
        
        {/* Enhanced Payment Summary */}
        {paymentAmounts && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-900 mb-3">{t('loans.detail.paymentBreakdown')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 rounded-lg p-3">
                <div className="text-lg font-semibold text-blue-600">
                  {formatCurrency(paymentAmounts.totalPrincipal)}
                </div>
                <div className="text-sm text-blue-700">{t('loans.detail.totalPrincipalPaid')}</div>
              </div>
              <div className="bg-purple-50 rounded-lg p-3">
                <div className="text-lg font-semibold text-purple-600">
                  {formatCurrency(paymentAmounts.totalInterest)}
                </div>
                <div className="text-sm text-purple-700">{t('loans.detail.totalInterestPaid')}</div>
              </div>
              <div className="bg-red-50 rounded-lg p-3">
                <div className="text-lg font-semibold text-red-600">
                  {formatCurrency(paymentAmounts.totalLateFees)}
                </div>
                <div className="text-sm text-red-700">{t('loans.detail.totalLateFees')}</div>
              </div>
            </div>
            {paymentAmounts.lastPaymentDate && (
              <div className="mt-3 text-sm text-gray-600">
                {t('loans.detail.lastPayment')}: {formatDate(paymentAmounts.lastPaymentDate)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Loan Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Basic Information */}
        <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">{t('loans.detail.basicInfo')}</h3>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-start space-x-3">
              <UserIcon className="h-5 w-5 text-gray-400 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-gray-900">
                  {t('loans.detail.borrower')}
                </div>
                <div className="text-sm text-gray-600">
                  {loan.borrower?.name || t('loans.list.unknownBorrower')}
                </div>
                {loan.borrower?.email && (
                  <div className="text-sm text-gray-500">
                    {loan.borrower.email}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-gray-900">
                  {t('loans.detail.location')}
                </div>
                <div className="text-sm text-gray-600">
                  {loan.location?.name || t('common.notFound')}
                </div>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <UserIcon className="h-5 w-5 text-gray-400 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-gray-900">
                  {t('loans.detail.loanOfficer')}
                </div>
                <div className="text-sm text-gray-600">
                  {loan.loanOfficer ? `${loan.loanOfficer.firstName} ${loan.loanOfficer.lastName}` : t('common.notFound')}
                </div>
              </div>
            </div>

            {loan.approver && (
              <div className="flex items-start space-x-3">
                <UserIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {t('loans.detail.approver')}
                  </div>
                  <div className="text-sm text-gray-600">
                    {`${loan.approver.firstName} ${loan.approver.lastName}`}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Financial Information */}
        <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">{t('loans.detail.financialInfo')}</h3>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">{t('loans.detail.monthlyInterestRate')}</span>
              <span className="text-sm font-medium text-gray-900">
                {formatPercentage(loan.monthlyInterestRate)}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">{t('loans.detail.paymentFrequency')}</span>
              <span className="text-sm font-medium text-gray-900">
                {t(`loans.paymentFrequency.${loan.paymentFrequency.toUpperCase()}`)}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-sm text-gray-500">{t('loans.detail.installments')}</span>
              <span className="text-sm font-medium text-gray-900">
                {loan.installments || 0}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">{t('loans.detail.scheduledPayment')}</span>
              <span className="text-sm font-medium text-gray-900">
                {formatCurrency(loan.scheduledPaymentAmount)}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">{t('loans.detail.totalInterestPaid')}</span>
              <span className="text-sm font-medium text-gray-900">
                {formatCurrency(loan.totalInterestPaid)}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">{t('loans.detail.totalLateFees')}</span>
              <span className="text-sm font-medium text-gray-900">
                {formatCurrency(loan.totalLateFees)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Dates */}
      <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{t('loans.detail.dates')}</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="flex items-start space-x-3">
              <CalendarIcon className="h-5 w-5 text-gray-400 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-gray-900">
                  {t('loans.detail.startDate')}
                </div>
                <div className="text-sm text-gray-600">
                  {formatDate(loan.startDate)}
                </div>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <CalendarIcon className="h-5 w-5 text-gray-400 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-gray-900">
                  {t('loans.detail.endDate')}
                </div>
                <div className="text-sm text-gray-600">
                  {formatDate(loan.endDate)}
                </div>
              </div>
            </div>

            {loan.nextPaymentDue && (
              <div className="flex items-start space-x-3">
                <CalendarIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {t('loans.detail.nextPaymentDue')}
                  </div>
                  <div className="text-sm text-gray-600">
                    {formatDate(loan.nextPaymentDue)}
                  </div>
                </div>
              </div>
            )}

            {loan.lastPaymentDate && (
              <div className="flex items-start space-x-3">
                <CalendarIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {t('loans.detail.lastPayment')}
                  </div>
                  <div className="text-sm text-gray-600">
                    {formatDate(loan.lastPaymentDate)}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Terms and Notes */}
      {(loan.terms || loan.notes) && (
        <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">{t('loans.detail.termsAndNotes')}</h3>
          </div>
          <div className="p-6 space-y-6">
            {loan.terms && (
              <div>
                <div className="flex items-start space-x-3">
                  <DocumentTextIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900 mb-2">
                      {t('loans.detail.terms')}
                    </div>
                    <div className="text-sm text-gray-600 whitespace-pre-wrap">
                      {loan.terms}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {loan.notes && (
              <div>
                <div className="flex items-start space-x-3">
                  <DocumentTextIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900 mb-2">
                      {t('loans.detail.notes')}
                    </div>
                    <div className="text-sm text-gray-600 whitespace-pre-wrap">
                      {loan.notes}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Payment History */}
      {paymentHistory && paymentHistory.length > 0 && (
        <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              {t('loans.detail.paymentHistory')} ({paymentHistory.length})
            </h3>
          </div>
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {t('loans.paymentHistory.date')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {t('loans.paymentHistory.amount')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {t('loans.paymentHistory.principal')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {t('loans.paymentHistory.interest')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {t('loans.paymentHistory.lateFees')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {t('loans.paymentHistory.paymentMethod')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {t('loans.paymentHistory.receiptNumber')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {t('loans.paymentHistory.notes')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paymentHistory.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(payment.paymentDate)}
                      {payment.overdueDays > 0 && (
                        <div className="text-xs text-red-600">
                          {payment.overdueDays} {t('loans.paymentHistory.daysOverdue')}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(payment.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatCurrency(payment.principalAmount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatCurrency(payment.interestAmount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatCurrency(payment.lateFeeAmount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 capitalize">
                      {payment.paymentMethod.replace('_', ' ')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {payment.receiptNumber || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                      {payment.notes || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <LoanPaymentModal
          loan={loan}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  );
};

export default LoanDetail;
